import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  IconButton,
  Paper,
  InputAdornment,
  Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FuelSourceItem from './FuelSourceItem';

// Prevent mouse wheel scrolling on number inputs
const preventWheelChange = (e) => {
  e.target.blur();
};

const PreRaceFuelFields = ({ formData, onChange }) => (
  <Box sx={{ mt: 1, pl: { xs: 0, sm: 1 } }}>
    <FormControlLabel
      control={
        <Checkbox
          checked={!!formData.preRaceFuelEnabled}
          onChange={(e) => {
            const on = e.target.checked;
            onChange('preRaceFuelEnabled', on);
            if (
              on &&
              (formData.preRaceGelEquivalents === '' ||
                formData.preRaceGelEquivalents === undefined ||
                formData.preRaceGelEquivalents === null)
            ) {
              onChange('preRaceGelEquivalents', 1);
            }
          }}
        />
      }
      label="Include pre-race fuel"
    />
    {formData.preRaceFuelEnabled && (
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={7}>
          <TextField
            fullWidth
            size="small"
            label="What you’ll take (checklist)"
            value={formData.preRaceFuelDescription ?? ''}
            onChange={(e) => onChange('preRaceFuelDescription', e.target.value)}
            placeholder="e.g. Maurten 160, banana, pre-mix bottle"
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            size="small"
            label="Count as gel equivalents"
            value={
              formData.preRaceGelEquivalents === '' || formData.preRaceGelEquivalents === undefined
                ? ''
                : formData.preRaceGelEquivalents
            }
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                onChange('preRaceGelEquivalents', '');
                return;
              }
              const n = parseFloat(raw);
              onChange('preRaceGelEquivalents', Number.isNaN(n) ? 0 : Math.max(0, n));
            }}
            type="number"
            inputProps={{ min: 0, step: 0.5 }}
            fullWidth
            helperText="Added to total gels. Use 0 for checklist-only."
          />
        </Grid>
      </Grid>
    )}
  </Box>
);

const NutritionSection = ({ formData, onChange, onFuelSourceChange, onAddFuelSource, onRemoveFuelSource, errors = {} }) => {
  // Handle direct input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange(name, value);
  };

  // Get unit abbreviation
  const getUnitAbbreviation = (unit) => {
    switch(unit) {
      case 'miles': return 'mi';
      case 'kilometers': return 'km';
      case 'hours': return 'hr';
      default: return unit;
    }
  };

  return (
    <Box>
      
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Nutrition Strategy</Typography>
        <RadioGroup
          name="nutritionType"
          value={formData.nutritionType}
          onChange={handleInputChange}
        >
          <FormControlLabel 
            value="gelFrequency" 
            control={<Radio />} 
            label="Plan by gel frequency" 
          />
          <FormControlLabel 
            value="carbsPerHour" 
            control={<Radio />} 
            label="Plan by carbs per hour" 
          />
        </RadioGroup>
      </FormControl>
      
      {/* Gel Frequency Option */}
      {formData.nutritionType === 'gelFrequency' && (
        <Box sx={{ mb: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={4}>
                <TextField
                  label="Take gel every"
                  name="gelFrequency"
                  value={formData.gelFrequency || ''}
                  onChange={handleInputChange}
                  type="text"
                  InputProps={{
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    },
                    endAdornment: formData.gelFrequency ? (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {getUnitAbbreviation(formData.gelFrequencyUnit)}
                        </Typography>
                      </InputAdornment>
                    ) : null
                  }}
                  required={formData.nutritionType === 'gelFrequency'}
                  error={!!errors.gelFrequency}
                  helperText={errors.gelFrequency}
                  sx={{ mr: 1, width: '150px' }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Unit</InputLabel>
                  <Select
                    name="gelFrequencyUnit"
                    value={formData.gelFrequencyUnit}
                    onChange={handleInputChange}
                    label="Unit"
                  >
                    <MenuItem value="miles">Miles</MenuItem>
                    <MenuItem value="kilometers">Kilometers</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <PreRaceFuelFields formData={formData} onChange={onChange} />
              </Grid>
            </Grid>
          </Paper>
          
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Additional Fuel Sources (Optional)</Typography>
          
          {formData.additionalFuelSources.map((source, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <FuelSourceItem 
                source={source}
                index={index}
                isAdditional={true}
                onFuelSourceChange={onFuelSourceChange}
                onRemoveFuelSource={onRemoveFuelSource}
                gelFrequency={formData.gelFrequency}
                gelFrequencyUnit={formData.gelFrequencyUnit}
              />
            </Box>
          ))}
          
          {formData.additionalFuelSources.length === 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Click the + button below to add additional nutrition sources
              </Typography>
            </Paper>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <IconButton 
              color="primary" 
              onClick={() => onAddFuelSource(true)}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      )}
      
      {/* Carbs Per Hour Option */}
      {formData.nutritionType === 'carbsPerHour' && (
        <Box sx={{ mb: 3 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={4} md={3}>
                <TextField
                  label="Carbs per Hour"
                  name="carbsPerHour"
                  value={formData.carbsPerHour || ''}
                  onChange={handleInputChange}
                  type="text"
                  InputProps={{
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          g/hr
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                  required={formData.nutritionType === 'carbsPerHour'}
                  error={!!errors.carbsPerHour}
                  helperText={errors.carbsPerHour}
                  sx={{ mr: 1, width: '150px' }}
                />
              </Grid>
              <Grid item xs={12}>
                <PreRaceFuelFields formData={formData} onChange={onChange} />
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 2 }}>Fuel Sources</Typography>
          
          {formData.fuelSources.map((source, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <FuelSourceItem 
                source={source}
                index={index}
                isAdditional={false}
                onFuelSourceChange={onFuelSourceChange}
                onRemoveFuelSource={onRemoveFuelSource}
              />
            </Box>
          ))}
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <IconButton 
              color="primary" 
              onClick={() => onAddFuelSource(false)}
              size="medium"
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NutritionSection; 